import React from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { SearchableCheckboxDropdown } from '@repo/videa-components/src/index';
import { Label } from '@repo/videa-components/src/ui/label';

import { InteractibleChip } from '@/common-components/elements/InteractibleChip/InteractibleChip';
import type { Procedure } from '@/react-query/useReferralInfo';

interface TreatmentSelectorProps {
  treatments: Procedure[];
  selectedTreatments: Set<number>;
  onTreatmentToggle: (treatmentId: number) => void;
  isLoading?: boolean;
  required?: boolean;
}

export const TreatmentSelector: React.FC<TreatmentSelectorProps> = ({
  treatments = [],
  selectedTreatments,
  onTreatmentToggle,
  isLoading = false,
  required = false,
}) => {
  const selectedItems = treatments.filter((treatment) =>
    selectedTreatments.has(treatment.procedure_id),
  );

  const { t } = useLingui();

  const handleChange = (items: Procedure[]) => {
    // Convert the array of selected items back to a Set of IDs
    const newSelectedIds = new Set(items.map((item) => item.procedure_id));

    // Find the treatment that was toggled by comparing with previous selection
    const toggledTreatment =
      items.find((item) => !selectedTreatments.has(item.procedure_id)) ||
      selectedItems.find((item) => !newSelectedIds.has(item.procedure_id));

    if (toggledTreatment) {
      onTreatmentToggle(toggledTreatment.procedure_id);
    }
  };

  if (isLoading) {
    return (
      <div className="vai-flex vai-flex-col vai-gap-2">
        {required && (
          <Label className="vai-text-sm">
            <Trans>Procedures</Trans>
            <span className="vai-text-red-500">*</span>
          </Label>
        )}
        <div className="vai-flex vai-items-center vai-justify-center vai-h-12 vai-bg-gray-50 vai-rounded-md vai-border vai-border-dashed vai-border-gray-300">
          <div className="vai-text-gray-500 vai-text-sm">
            {t`Loading procedures...`}
          </div>
        </div>
      </div>
    );
  }

  // Ensure we have valid treatments data
  if (!treatments || treatments.length === 0) {
    return (
      <div className="vai-flex vai-flex-col vai-gap-2">
        {required && (
          <Label className="vai-text-sm">
            <Trans>Procedures</Trans>
            <span className="vai-text-red-500">*</span>
          </Label>
        )}
        <div className="vai-flex vai-items-center vai-justify-center vai-h-12 vai-bg-gray-50 vai-rounded-md vai-border vai-border-dashed vai-border-gray-300">
          <div className="vai-text-gray-500 vai-text-sm">
            {t`No procedures available`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vai-flex vai-flex-col vai-gap-2">
      {required && (
        <Label className="vai-text-sm">
          <Trans>Procedures</Trans>
          <span className="vai-text-red-500">*</span>
        </Label>
      )}
      <SearchableCheckboxDropdown
        items={treatments}
        selectedItems={selectedItems}
        onChange={handleChange}
        getItemLabel={(item) => item.procedure_name || ''}
        getItemValue={(item) => item.procedure_id?.toString() || ''}
        placeholder={
          selectedItems.length > 0
            ? `${selectedItems.length} ${t`procedures selected`}`
            : t`Select procedures`
        }
        buttonClassName="short"
        selectionPhrase="Procedure"
      />
      {selectedItems.length > 0 && (
        <div className="vai-flex vai-flex-wrap vai-gap-2 vai-mt-2">
          {selectedItems.map((treatment) => (
            <InteractibleChip
              key={treatment.procedure_id}
              label={treatment.procedure_name || ''}
              onDelete={() => onTreatmentToggle(treatment.procedure_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
